import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@nvision/identity-core';
import { Button, Spinner } from '@nvision/ui';

import { Card } from '@/components/card';
import { Page } from '@/components/page';

import { getDefaultPricingsFormValues, usePricingsFilters } from '../../hooks';
import { PricingsSearchFiltersForm, type PricingsSearchFiltersFormRef } from '../forms';

import styles from './pricings.module.css';

export const Pricings: React.FC = () => {
  const { clearFilters, customerKey, filters, onFiltersChange } = usePricingsFilters();
  const { t } = useTranslation();
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const formRef = useRef<PricingsSearchFiltersFormRef>({} as PricingsSearchFiltersFormRef);
  const prevCustomerKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevCustomerKeyRef.current !== null && prevCustomerKeyRef.current !== customerKey && customerKey) {
      formRef.current?.reset(getDefaultPricingsFormValues(timeZone));
      clearFilters();
    }

    if (customerKey) {
      prevCustomerKeyRef.current = customerKey;
    }
  }, [customerKey, clearFilters, timeZone]);

  return (
    <Page>
      <Page.SideBar>
        <Page.SideBar.Header title="Pricing Search" />
        <Page.SideBar.Main>
          {!filters ? (
            <div className={styles.loading}>
              <Spinner size="lg" />
            </div>
          ) : (
            <PricingsSearchFiltersForm
              customerKey={customerKey}
              formRef={formRef}
              initialValues={filters}
              onSubmit={onFiltersChange}
            />
          )}
        </Page.SideBar.Main>
        <Page.SideBar.Footer>
          <Button secondary size="sm" onClick={() => formRef.current?.reset(getDefaultPricingsFormValues(timeZone))}>
            {t('translation:default.buttons.clear')}
          </Button>

          <Button type="submit" size="sm" primary onPress={() => formRef.current?.submit()}>
            {t('translation:default.buttons.search')}
          </Button>
        </Page.SideBar.Footer>
      </Page.SideBar>
      <Page.Main>
        {/*TODO: move it to the PricingControls when it will be created*/}
        <Card className={styles.results}>
          <Card.Header title="Pricing Search Results" />
        </Card>
      </Page.Main>
    </Page>
  );
};
